"use client";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { env } from "next-runtime-env";
import { useState } from "react";
import Image from "next/image";

export function IntegrationsSection() {
  const DASHBOARD_URL =
    env("NEXT_PUBLIC_DASHBOARD_URL") || "https://cloud.getmac.io";
  const [platform, setPlatform] = useState<"github" | "gitlab">("github");

  const steps =
    platform === "github"
      ? [
        {
          number: 1,
          title: "Install our GitHub App",
          description: (
            <>
              Connect your GitHub account with GetMac by installing our GitHub
              App. <br />
            </>
          ),
        },
        {
          number: 2,
          title: "Update your workflows",
          description: (
            <>
              Modify your GitHub Actions workflows to use our custom runner
              label. For example:
              <div className="mb-6 mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-sm md:text-base font-mono font-medium">
                <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2 rounded whitespace-nowrap">
                  runs-on: macos-latest
                </div>
                <div className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 rounded mt-1 whitespace-nowrap">
                  runs-on: getmac
                </div>
              </div>
              For more details, check out our{" "}
              <Link
                href="/docs"
                className="text-primary font-medium underline"
              >
                documentation
              </Link>
              .
            </>
          ),
        },
        {
          number: 3,
          title: "You’re all set!",
          description: "Experience up to 3x faster build times with GetMac!",
        },
      ]
      : [
        {
          number: 1,
          title: "Install the GitLab CI/CD runner",
          description: (
            <>
              Install the GitLab CI/CD runner by following the official GitLab{" "}
              <Link
                href="https://docs.gitlab.com/runner/install/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium underline"
              >
                installation guide
              </Link>
              .
            </>
          ),
        },
        {
          number: 2,
          title: "Install our custom executor",
          description: (
            <>
              Configure the GitLab runner to use our custom executor by
              following the instructions in our{" "}
              <Link
                href="https://github.com/getmac-io/getmac-gitlab-executor"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium underline"
              >
                repository
              </Link>
              .
            </>
          ),
        },
        {
          number: 3,
          title: "You’re all set!",
          description: "Experience up to 3x faster build times with GetMac!",
        },
      ];

  return (
    <section className="py-24">
      <header className="text-center space-y-4 mb-8 md:mb-16 px-4">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl mx-auto">
          Integrates with{" "}
          {platform === "github" ? "GitHub Actions" : "GitLab CI/CD"}
        </h2>
        <p className="text-muted-foreground text-base sm:text-lg md:text-xl lg:text-2xl max-w-4xl mx-auto">
          Switch to GetMac runners in minutes, not hours, and experience up to{" "}
          <strong className="font-semibold text-primary">3x faster</strong>{" "}
          build times for your{" "}
          {platform === "github" ? "GitHub Actions" : "GitLab CI/CD"} workflows.
        </p>
        <div className="flex justify-center items-center gap-4 mt-6">
          <span className="text-base font-medium text-gray-700 dark:text-gray-300">
            I&apos;m using:
          </span>
          <Button
            variant={platform === "github" ? "default" : "outline"}
            className={`rounded-full px-4 py-2 font-medium transition ${platform === "github" ? "bg-primary text-white" : ""}`}
            onClick={() => setPlatform("github")}
          >
            GitHub
          </Button>
          <Button
            variant={platform === "gitlab" ? "default" : "outline"}
            className={`rounded-full px-4 py-2 font-medium transition ${platform === "gitlab" ? "bg-primary text-white" : ""}`}
            onClick={() => setPlatform("gitlab")}
          >
            GitLab
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch px-4">
        {/* Left: Illustration */}
        <div className="flex h-full items-center justify-center order-2 lg:order-1">
          <Image
            src="/images/integrations-light.svg"
            alt="Integrations"
            width={450}
            height={650}
            className="dark:hidden block max-h-[500px] lg:max-h-[800px] mx-auto w-auto"
            loading="lazy"
          />
          <Image
            src="/images/integrations-dark.svg"
            alt="Integrations Dark"
            width={450}
            height={650}
            className="hidden dark:block max-h-[500px] lg:max-h-[800px] mx-auto w-auto"
            loading="lazy"
          />
        </div>
        {/* Right: Steps as text/description */}
        <div className="flex flex-col h-full order-1 lg:order-2">
          {steps.map((step, idx) => (
            <div
              key={step.number}
              className={`group flex items-start gap-4 relative ${idx < steps.length - 1 ? "flex-1" : ""}`}
            >
              {/* Dot and line */}
              <div className="flex flex-col items-center h-full">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary text-white font-bold text-xl shadow shrink-0 z-10
                  transition-transform duration-200 group-hover:scale-110 group-hover:shadow-xl"
                >
                  {step.number}
                </div>
                {/* Line: only for non-last step */}
                {idx < steps.length - 1 && (
                  <div className="flex-grow w-1 bg-primary/30" />
                )}
              </div>
              {/* Step content */}
              <div className="transition-all duration-200 group-hover:-translate-y-1 group-hover:text-primary">
                <div className="font-semibold text-base sm:text-lg md:text-xl mb-1 text-gray-900 dark:text-gray-100">
                  {step.title}
                </div>
                <div className="text-sm sm:text-base md:text-lg text-muted-foreground">
                  {step.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 md:mt-16 w-full px-4">
        <Button
          asChild
          className="rounded-full bg-primary hover:bg-primary/90 text-white h-12 sm:h-14 text-sm sm:text-base md:text-lg px-6 sm:px-8 md:px-12 w-full sm:w-auto"
        >
          <Link href={DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
            Speed Up {platform === "github" ? "GitHub Actions" : "GitLab CI/CD"}
          </Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="rounded-full h-12 sm:h-14 text-sm sm:text-base md:text-lg px-6 sm:px-8 md:px-12 w-full sm:w-auto border-2 border-primary/80 hover:border-primary hover:bg-primary/10 dark:border-primary dark:bg-primary/10 dark:hover:bg-primary/30 font-semibold transition-all duration-200"
        >
          <Link href="/pricing/#cost-calculator">Estimate your savings</Link>
        </Button>
      </div>
    </section>
  );
}
